<script lang="ts" setup>
  import { useSpeechToText } from '@/composables/useSpeechToText';

  defineOptions({
    name: 'AppTextarea',
    inheritAttrs: false
  });

  const attrs = useAttrs();
  const slots = useSlots();

  // Generated once per component instance — useAttrs() is treated as changed on
  // nearly every parent re-render, so computing this suffix inside the computed
  // itself made the id drift on every render. Vuetify's VForm registers a field's
  // validation state under the id captured at mount but unregisters it under
  // whatever id is current at unmount, so a drifting id meant unregister silently
  // matched nothing, leaving a stale (and often invalid) entry in the form forever.
  const idSuffix = Math.random().toString(36).slice(2, 7);

  const elementId = computed(() => {
    const _elementIdToken = attrs.id || attrs.label;

    return _elementIdToken ? `app-textarea-${_elementIdToken}-${idSuffix}` : undefined;
  });

  const label = computed(() => attrs.label as string | undefined);
  const mandatory = computed(() => attrs.mandatory as boolean);
  const spellcheckAttr = computed(() => (attrs.spellcheck !== undefined ? attrs.spellcheck : true));
  const langAttr = computed(() => (attrs.lang !== undefined ? attrs.lang : 'en-GB'));
  const textAreaRef = ref();

  const slotsWithoutAppendInner = computed(() =>
    Object.fromEntries(Object.entries(slots).filter(([name]) => name !== 'append-inner'))
  );

  const appendToModel = (text: string) => {
    const current = (attrs.modelValue as string) || '';
    const newValue = current ? `${current} ${text}` : text;
    const handler = (attrs as any)['onUpdate:modelValue'];

    if (typeof handler === 'function') handler(newValue);
    else if (Array.isArray(handler)) handler.forEach((fn: Function) => fn(newValue));
  };

  const { isListening, interimTranscript, error, isSupported, toggle } = useSpeechToText({
    onFinalResult: appendToModel
  });

  const formatValue = () => {
    const input = textAreaRef.value?.$el?.querySelector('textarea');
    if (input) {
      input.value = input.value
        ?.split('\n')
        .map((line: string) => line.replace(/[^\S\n]+/g, ' ').trim())
        .join('\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
      input.dispatchEvent(new Event('input'));
    }
  };

  onMounted(() => {
    const input = textAreaRef.value?.$el?.querySelector('textarea');
    if (input) {
      input.addEventListener('blur', formatValue);
    }
  });
</script>

<template>
  <div class="app-textarea flex-grow-1" :class="$attrs.class">
    <VLabel v-if="label" :for="elementId" class="mb-1 text-body-2 text-high-emphasis">
      {{ label }}
      <span v-if="mandatory" class="text-h6 text-error ms-1">*</span>
    </VLabel>

    <!-- v-allowed-text -->
    <VTextarea
      ref="textAreaRef"
      v-bind="{
        ...$attrs,
        class: null,
        label: undefined,
        variant: 'outlined',
        id: elementId,
        spellcheck: spellcheckAttr,
        lang: langAttr
      }"
    >
      <template v-for="(_, name) in slotsWithoutAppendInner" #[name]="slotProps">
        <slot :name="name" v-bind="slotProps || {}" />
      </template>

      <template #append-inner>
        <slot name="append-inner" />
        <div v-if="isSupported && !$attrs.readonly" class="d-flex align-center">
          <span
            v-if="interimTranscript"
            class="text-body-2 text-disabled font-italic me-2 text-truncate d-inline-block"
            style="max-width: 150px"
          >
            {{ interimTranscript }}
          </span>
          <VBtn
            icon
            size="x-small"
            :variant="isListening ? 'tonal' : 'text'"
            :color="isListening ? 'info' : 'secondary'"
            @click.stop="toggle"
          >
            <VIcon :icon="isListening ? 'tabler-microphone-off' : 'tabler-microphone'" size="18" />
            <VTooltip activator="parent" :text="isListening ? 'Stop recording' : 'Speak to type'" />
          </VBtn>
        </div>
      </template>
    </VTextarea>

    <div v-if="error" class="text-caption text-error mt-1 ms-1">Microphone error: {{ error }}</div>
  </div>
</template>
